import type { SurveyResponse, CategoryScore } from './surveyTypes';

// Category definitions for score calculation
const categoryDefinitions = [
  {
    name: 'Intrinsic Goal Orientation',
    items: [1, 22, 24],
    description: 'Measures the degree to which you perceive yourself participating in academic tasks for reasons such as challenge, curiosity, and mastery.'
  },
  {
    name: 'Extrinsic Goal Motivation',
    items: [11, 13, 30],
    description: 'Measures the degree to which you perceive yourself participating in academic tasks for reasons such as grades, rewards, performance, and competition.'
  },
  {
    name: 'Task Value',
    items: [4, 17, 23],
    description: 'Measures how interesting, important, and useful you find the course material.'
  },
  {
    name: 'Control of Learning Beliefs',
    items: [2, 9, 25],
    description: 'Measures your beliefs about whether your academic success is controlled by your own efforts.'
  },
  {
    name: 'Self-Efficacy for Learning and Performance',
    items: [5, 6, 12, 15, 20, 21, 29, 31],
    description: 'Measures your beliefs about your ability to accomplish course objectives.'
  },
  {
    name: 'Test Anxiety',
    items: [3, 8, 14, 19, 28, 120, 121, 122],
    description: 'Measures your worry about test performance.'
  },
  {
    name: 'Rehearsal',
    items: [39, 46, 59, 72],
    description: 'Measures your use of repetition to remember academic material.'
  },
  {
    name: 'Elaboration',
    items: [51, 61, 62, 64, 69, 81],
    description: 'Measures your ability to build internal connections between items to be learned using strategies like paraphrasing, summarizing, creating analogies, and generative note-taking to connect new material to prior knowledge.'
  },
  {
    name: 'Organization',
    items: [32, 42, 49, 53, 63, 67],
    description: 'Measures your ability to select appropriate information and construct connections.'
  },
  {
    name: 'Critical Thinking',
    items: [36, 38, 47, 66, 71],
    description: 'Measures your ability to apply previous knowledge to new situations in order to solve problems, reach decisions, or make critical evaluations with respect to standards of excellence.'
  },
  {
    name: 'Metacognitive Self-Regulation',
    items: [33, 41, 44, 54, 55, 56, 57, 76, 78, 79],
    description: 'Measures your awareness, knowledge, and control of cognition.'
  },
  {
    name: 'Time and Study Environment',
    items: [35, 43, 52, 65, 70, 73, 77, 80],
    description: 'Measures your ability to manage and regulate your study time and environment.'
  },
  {
    name: 'Effort Regulation',
    items: [37, 48, 60, 74],
    description: 'Measures your ability to control your effort and attention when faced with distractions or uninteresting tasks.'
  },
  {
    name: 'Peer Learning',
    items: [34, 45, 50, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114],
    description: 'Measures your collaboration with peers in learning.'
  },
  {
    name: 'Help Seeking',
    items: [40, 58, 68, 75, 115, 116, 117, 118, 119],
    description: 'Measures your ability to manage the support of peers and instructors.'
  }
];

// Questions that need reverse scoring (8 - original_rating)
const reverseScoreQuestions = new Set([33, 57, 52, 80, 60, 40, 42, 48, 73, 76, 77, 88, 113, 114]);

/**
 * Calculate category scores from survey responses
 * @param responses Array of survey responses with questionId and rating
 * @returns Array of calculated category scores
 */
export function calculateScores(responses: SurveyResponse[]): CategoryScore[] {
  return categoryDefinitions.map(category => {
    const relevantResponses = responses.filter(r => 
      category.items.includes(r.questionId)
    ).map(response => ({
      ...response,
      // Apply reverse scoring if needed
      rating: reverseScoreQuestions.has(response.questionId) 
        ? 8 - response.rating 
        : response.rating
    }));
    
    // Calculate mean score if there are responses, otherwise 0
    const score = relevantResponses.length > 0
      ? relevantResponses.reduce((sum, r) => sum + r.rating, 0) / relevantResponses.length
      : 0;

    return {
      name: category.name,
      score: Number(score.toFixed(2)), // Round to 2 decimal places
      items: category.items,
      maxPossible: 7, // Maximum possible mean score
      description: category.description
    };
  });
}

/**
 * Calculate completion percentage based on total questions
 * @param responses Array of survey responses
 * @returns Completion percentage (0-100)
 */
export function calculateCompletionPercentage(responses: SurveyResponse[]): number {
  const totalQuestions = 114;
  return Math.round((responses.length / totalQuestions) * 100);
}

/**
 * Get responses for a specific category
 * @param responses Array of survey responses
 * @param categoryName Name of the category
 * @returns Filtered responses for the category
 */
export function getResponsesForCategory(responses: SurveyResponse[], categoryName: string): SurveyResponse[] {
  const category = categoryDefinitions.find(cat => cat.name === categoryName);
  if (!category) return [];
  
  return responses.filter(r => category.items.includes(r.questionId));
}

/**
 * Check if a question requires reverse scoring
 * @param questionId ID of the question
 * @returns True if question needs reverse scoring
 */
export function isReverseScored(questionId: number): boolean {
  return reverseScoreQuestions.has(questionId);
}

/**
 * Get all category definitions
 * @returns Array of category definitions
 */
export function getCategoryDefinitions() {
  return categoryDefinitions;
}

/**
 * Validate survey responses for completeness
 * @param responses Array of survey responses
 * @returns Validation result with missing questions
 */
export function validateSurveyResponses(responses: SurveyResponse[]): {
  isComplete: boolean;
  totalQuestions: number;
  answeredQuestions: number;
  missingQuestions: number[];
  completionPercentage: number;
} {
  const totalQuestions = 114;
  const allQuestionIds = Array.from({ length: totalQuestions }, (_, i) => i + 1);
  const answeredIds = new Set(responses.map(r => r.questionId));
  const missingQuestions = allQuestionIds.filter(id => !answeredIds.has(id));
  
  return {
    isComplete: missingQuestions.length === 0,
    totalQuestions,
    answeredQuestions: responses.length,
    missingQuestions,
    completionPercentage: calculateCompletionPercentage(responses)
  };
}